import { useState } from 'react';
import OptimoCapitalLogoWB from '../../logo/OptimoCapitalLogoWB.svg';
import OptimoCapitalLogoWY from '../../logo/OptimoCapitalLogoWY.svg';
import LogoutMenu from './Logout';
import Notifications from './Notifications';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import LanguageSelector from './LanguageSelector';
import FloatingReferButton from '../referEarnFloating';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from "@mui/material/styles";
import { Link, useLocation } from 'react-router-dom';
import { Menu } from '@mui/icons-material';
import { useAuth } from '../../services/auth/AuthContext';

const Navbar = ({ isTransparent = true, showLogout = true }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  
  const resellerDashboardMenuItems = [
    { label: t('navbar.menu_items.dashboard'), link: "/dashboard" },
    { label: t('navbar.menu_items.profile'), link: "/profile" },
    { label: t('search'), link: "/search" },
    ...(
      (user?.role?.includes('support') ||
       user?.role?.includes('connector') ||
       user?.role?.includes('partner') ||
        user?.role?.includes('bc_partner') ||
       user?.role?.includes('rm'))
        ? [
            { label: t('Create Enquiry'), link: "/create-enquiry" },
          ]
        : []
    ),
    ...(user?.role.includes('support')
      ? [
        { label: "Support", link: "/support" },
        { label: "Callbacks" , link: "/callbacks" },
      ]
      : []
    ),
  ];

  const isResellerDashboard = resellerDashboardMenuItems.some(
    ({ link }) => location.pathname.startsWith(link)
  );
  const showResellerDashboard = isResellerDashboard &&
    user.user_profile.onboarding_status === 'approved';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  return (
    <AppBar
      position="static"
      color="transparent"
      sx={{
        color: 'text.primary',
        p: '0.75rem 1.25rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: alpha(theme.palette.primary.main, 0.28),
        // Three layers, all cast downward: a hairline that defines the bottom
        // edge, a tight contact shadow, then a wide ambient one. The previous
        // single 12px blur sat at y:1, so it haloed upward as much as down and
        // read as a blur rather than an edge.
        //
        // Tinted from the brand dark rather than pure black: the surfaces here
        // are warm (#FFF9F7), and a neutral black shadow over them goes muddy
        // grey.
        boxShadow: [
          `0 1px 0 ${alpha(theme.palette.primary.dark, 0.1)}`,
          `0 2px 4px ${alpha(theme.palette.primary.dark, 0.08)}`,
          `0 8px 24px ${alpha(theme.palette.primary.dark, 0.1)}`,
        ].join(', '),
      }}
    >
      <Link to="/">
        <img
          src={OptimoCapitalLogoWY}
          alt="Optimo Logo"
          style={{ width: '100px', objectFit: 'contain' }}
        />
      </Link>
      {showResellerDashboard && (
        <>
          <Box flexGrow={1} />
          {/* Desktop Menu */}
          {!isMobile && (
            <Box display="flex" gap={0.5} alignItems="center">
              {resellerDashboardMenuItems.map((item, index) => {
                const isActive = location.pathname === item.link;
                return (
                  <Button
                    key={index}
                    variant="text"
                    component={Link}
                    to={item.link}
                    disableRipple
                    sx={{
                      px: 1.75,
                      py: 0.75,
                      borderRadius: 999,
                      // Constant weight: switching tabs must not reflow the row.
                      fontWeight: 600,
                      color: isActive
                        ? theme.palette.primary.contrastText
                        : alpha(theme.palette.text.primary, 0.72),
                      backgroundColor: isActive
                        ? theme.palette.primary.main
                        : 'transparent',
                      boxShadow: isActive
                        ? `0 1px 2px ${alpha(theme.palette.primary.dark, 0.32)}`
                        : 'none',
                      "&:hover": {
                        backgroundColor: isActive
                          ? theme.palette.primary.dark
                          : alpha(theme.palette.common.white, 0.45),
                        color: isActive
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              {/* Notifications Icon with consistent spacing */}
              <Notifications />
            </Box>
          )}
        </>
      )}

      {!isMobile && (
        <Box display='flex' alignItems='center'>
          <LanguageSelector />
          {showLogout && (
            <LogoutMenu user={user} />
          )}
        </Box>
      )}
      
      {/* Mobile Menu Icon */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="menu"
          onClick={toggleDrawer(true)}
        >
          <Menu />
        </IconButton>
      )}
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            pb: 1,
            width: "250px",
            justifyContent: 'space-between',
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <List>
          <ListItem>
            <img
              src={OptimoCapitalLogoWB}
              alt="Optimo Logo"
              style={{ width: '80px', objectFit: 'contain' }}
            />
          </ListItem>
          {user && (
            <>
              <ListItem>
                {t('welcome')}, {user.user_profile.first_name}
              </ListItem>

              <Divider sx={{ mx: 1, mb: 2 }} />
            </>
          )}

          {showResellerDashboard && (
            <>
              {resellerDashboardMenuItems.map((item, index) => {
                const isActive = location.pathname === item.link;
                return (
                  <ListItem
                    key={index}
                    component={Link}
                    to={item.link}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      width: 'auto',
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.14)
                        : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        style: {
                          color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                          fontWeight: isActive ? 600 : 500,
                        },
                      }}
                    />
                  </ListItem>
                );
              })}

              <Divider sx={{ mx: 1, mb: 2 }} />
            </>
          )}

          {showLogout && (
            <ListItemButton onClick={logout}>
              {t('logout')}
            </ListItemButton>
          )}
        </List>

        <LanguageSelector />
      </Drawer>

      <FloatingReferButton user={user} />
    </AppBar>
  );
};

export default Navbar;